import { useState, useEffect } from 'react';
import { logger } from '@/lib/logger';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ArrowLeft } from 'lucide-react';
import { useOrganization, useUser } from '@clerk/clerk-react';
import { createPortalioClient } from '@/services/portalio/client';
import { createImportService } from '@/services/portalio/importService';
import { toast } from 'sonner';
import { useAppStore } from '@/stores/appStore';
import { useFloorPlanContext } from '../FloorPlanProvider';
import { PortalProposalSelector } from './PortalProposalSelector';
import { PortalProductSelector } from './PortalProductSelector';
import type { CategorySelection } from './CategoryDropdown';

interface PortalProductImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onProductsImported?: () => void;
  preselectedProposalId?: number;
  preselectedProposal?: Proposal;
  placedProductIds?: string[];
  skipToProducts?: boolean;
}

interface Proposal {
  id: number;
  proposalName: string;
  proposalNumber: number;
  customerName: string;
  status: string;
  totalAmount: number;
  createdDate: string;
}

interface ProductSelection {
  productId: string;
  category: CategorySelection | null;
  referenceId?: string;
}

type Step = 'proposals' | 'products';

export function PortalProductImportModal({
  isOpen,
  onClose,
  onProductsImported,
  preselectedProposalId,
  preselectedProposal,
  placedProductIds = [],
  skipToProducts = false,
}: PortalProductImportModalProps) {
  const { organization } = useOrganization();
  const { user } = useUser();
  const { floorPlan } = useFloorPlanContext();
  const setActiveDrawer = useAppStore(state => state.setActiveDrawer);
  const [currentStep, setCurrentStep] = useState<Step>(
    skipToProducts || preselectedProposal ? 'products' : 'proposals',
  );
  const [selectedProposal, setSelectedProposal] = useState<Proposal | null>(
    preselectedProposal || null,
  );

  // Update state when preselectedProposal changes
  useEffect(() => {
    if (preselectedProposal) {
      setSelectedProposal(preselectedProposal);
      setCurrentStep('products');
    }
  }, [preselectedProposal]);

  // Reset state when modal closes
  const handleClose = () => {
    setCurrentStep(preselectedProposal ? 'products' : 'proposals');
    setSelectedProposal(preselectedProposal || null);
    onClose();
  };

  // Handle proposal selection
  const handleSelectProposal = (proposal: Proposal) => {
    setSelectedProposal(proposal);
    setCurrentStep('products');
  };

  // Handle back button
  const handleBack = () => {
    setCurrentStep('proposals');
  };

  // Handle import with category mappings
  const handleImport = async (selections: ProductSelection[]) => {
    if (!organization?.id || !selectedProposal) {
      toast.error('Missing organization or proposal information');
      return;
    }

    if (!user?.id) {
      logger.error('User context missing during import', {
        organizationId: organization?.id,
        proposalId: selectedProposal?.id,
      });
      toast.error('Authentication error - please refresh the page and try again');
      return;
    }

    logger.debug('Starting product import', {
      userId: user.id,
      organizationId: organization.id,
      proposalId: selectedProposal.id,
      selectionCount: selections.length,
    });

    try {
      // Get proposal details to extract products
      const client = createPortalioClient(organization.id);
      const proposalDetails = await client.getProposalDetails(selectedProposal.id);

      // Flatten all products from the proposal
      const allProducts = [];
      proposalDetails.areas?.forEach(area => {
        area.options?.forEach(option => {
          option.items?.forEach(item => {
            allProducts.push({
              ...item,
              area: area.areaName,
              option: option.optionName,
            });
          });
        });
      });

      // Filter to only selected products
      const selectedProductIds = new Set(selections.map(s => s.productId));
      const productsToImport = allProducts.filter(p => selectedProductIds.has(p.id));

      // Use import service to handle the import - pass user ID for proper auth context
      const importService = createImportService(organization.id, floorPlan?.id, user.id);
      const result = await importService.importProducts(
        productsToImport,
        selections,
        selectedProposal.id,
        selectedProposal.proposalName,
      );

      if (result.success) {
        toast.success(`Successfully imported ${result.importedCount} products`);

        // Open the Portal products drawer to show imported items
        setActiveDrawer('portalProducts');

        // Trigger callback and close
        onProductsImported?.();
        handleClose();
      } else {
        toast.error('Failed to import products');
      }

      // Show any specific errors
      result.errors?.forEach(error => {
        toast.error(error);
      });
    } catch (error: any) {
      logger.error('Portal product import failed:', {
        error: error.message,
        stack: error.stack,
        organizationId: organization?.id,
        userId: user?.id,
        proposalId: selectedProposal?.id,
      });

      // Provide more specific error message if available
      const errorMessage = error.message || 'Failed to import products. Please try again.';
      toast.error(errorMessage);

      // Log to monitoring system if needed
      if (error.message?.includes('authentication') || error.message?.includes('unauthorized')) {
        logger.warn('Authentication issue detected during import - user may need to re-login');
      }
    }
  };

  if (!organization?.id) {
    return null;
  }

  return (
    <Dialog open={isOpen} onOpenChange={open => !open && handleClose()}>
      <DialogContent
        className="max-w-4xl max-h-[90vh] flex flex-col"
        onEscapeKeyDown={e => {
          e.preventDefault();
          handleClose();
        }}
      >
        <DialogHeader className="pb-4">
          <DialogTitle>
            {currentStep === 'proposals'
              ? 'Select Portal.io Proposal'
              : `Import Products from Proposal #${selectedProposal?.proposalNumber}`}
          </DialogTitle>
          <DialogDescription>
            {currentStep === 'proposals'
              ? 'Choose a proposal to import products from'
              : 'Select products and assign categories for import'}
          </DialogDescription>
        </DialogHeader>

        {currentStep === 'products' && selectedProposal && (
          <div className="flex items-center gap-3 px-6 -mt-2">
            <Button variant="ghost" size="sm" onClick={handleBack} className="h-8 w-8 p-0">
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <span className="text-sm text-muted-foreground">Back to proposals</span>
          </div>
        )}

        <div className="flex-1 overflow-auto">
          {currentStep === 'proposals' ? (
            <PortalProposalSelector
              organizationId={organization.id}
              onSelectProposal={handleSelectProposal}
            />
          ) : (
            selectedProposal && (
              <PortalProductSelector
                organizationId={organization.id}
                proposalId={selectedProposal.id}
                proposalName={selectedProposal.proposalName}
                customerName={selectedProposal.customerName}
                totalAmount={selectedProposal.totalAmount}
                placedProductIds={placedProductIds}
                onBack={handleBack}
                onImport={handleImport}
              />
            )
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
